import { Form, Formik } from 'formik';
import { Button } from '@mui/material';

import { SelectField } from 'components';

import { useCrossEnvData } from './use-cross-env-data';
import { CrossEnvBaseValues, CrossEnvCopyFormProps } from './types';

export const CrossEnvCopyForm = <T extends CrossEnvBaseValues>({
  onClose,
  onSubmit,
  topContent,
  initialValues,
  bottomContent,
  validationSchema,
  showTenantSelect = true,
  showProjectSelect = false,
}: CrossEnvCopyFormProps<T>) => {
  const {
    isTenantsFetching,
    isProjectsFetching,
    destinationOptions,
    isDestinationsFetching,
    fetchDestinationTenants,
    fetchDestinationProjects,
    destinationTenantOptions,
    destinationProjectOptions,
  } = useCrossEnvData();

  return (
    <Formik<T>
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ dirty, values, isSubmitting, setFieldValue }) => {
        const selectedScope = values.scope ?? 'tenant';
        const shouldShowTenantSelect =
          showTenantSelect &&
          (!showProjectSelect || selectedScope === 'tenant');
        const shouldShowProjectSelect =
          showProjectSelect &&
          (!showTenantSelect || selectedScope === 'project');

        return (
          <Form className="mt-6 flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              {topContent}
              <SelectField
                fullWidth
                name="destinationName"
                options={destinationOptions}
                label="Destination environment"
                loading={isDestinationsFetching}
                onChange={(e) => {
                  const destination = String(e.target.value);
                  if (showTenantSelect) {
                    setFieldValue('destinationTenantId', '');
                    fetchDestinationTenants(destination);
                  }

                  if (showProjectSelect) {
                    setFieldValue('destinationProjectId', '');
                    fetchDestinationProjects(destination);
                  }
                }}
              />
              {shouldShowTenantSelect && values.destinationName && (
                <SelectField
                  fullWidth
                  name="destinationTenantId"
                  label="Target organization"
                  loading={isTenantsFetching}
                  options={destinationTenantOptions}
                />
              )}
              {shouldShowProjectSelect && values.destinationName && (
                <SelectField
                  fullWidth
                  label="Target project"
                  name="destinationProjectId"
                  loading={isProjectsFetching}
                  options={destinationProjectOptions}
                />
              )}
              {bottomContent}
            </div>
            <div className="flex justify-end gap-4">
              <Button color="error" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={!dirty || isSubmitting}>
                Copy
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export type { CrossEnvBaseValues } from './types';
